import { PassengerType } from "../../constants/settings";
import { jsonHelper } from "../../helpers";
import { Booking, Leg, Passenger, Segment, Service } from "../../state/models";
import { Ancillary, AnciPassengerKeys, AnciService } from "../../state/models/Ancillary";

type ServiceType = 'baggages' | 'meals' | 'insurances' | 'lounges' | 'hotels' | 'counterCheckin';

interface FindServiceArgs {
  logicalFlightId: number;
  physicalFlightId?: number;
  passengerId: number;
  ssrCode: string;
}

export interface FindServiceData {
  segment: Segment;
  leg?: Leg;
  passenger: Passenger;
  service: Service;
}

export function findAddedService( booking: Booking, type: ServiceType, services: FindServiceArgs[]): FindServiceData[] {

  if(!Array.isArray(booking?.passengers) || booking.passengers.length === 0) return [];
  if(!Array.isArray(booking?.segments) || booking.segments.length === 0) return [];

  const { passengers, segments } = booking;

  const items = services.map(s => {
    const { logicalFlightId, physicalFlightId, passengerId, ssrCode } = s;

    const segment = segments.find(s => s.logicalFlightId === logicalFlightId);
    if(!segment) return;

    const leg = !physicalFlightId ? undefined : segment.legs.find(l => l.physicalFlightId === physicalFlightId);
  
    const passenger = passengers.find(p => p.id === passengerId);
    if(!passenger) return;
  
    const passengerServices = passenger[type];
    if(!Array.isArray(passengerServices) || passengerServices.length === 0) return;
  
    const service = passengerServices.find(s => s.logicalFlightId === logicalFlightId && s.ssrCode === ssrCode);
    if(!service) return;

    return { segment, leg, service, passenger };

  });

  return items.filter(x => !!x);

}

export function findAddedServiceAll(booking: Booking, type: ServiceType, logicalFlightId?: number): FindServiceData[] {
  
  if(!Array.isArray(booking?.passengers) || booking.passengers.length === 0) return [];
  if(!Array.isArray(booking?.segments) || booking.segments.length === 0) return [];

  const { passengers, segments } = booking;
  let services = passengers
    .filter(p => Array.isArray(p[type]) && p[type].length > 0)
    .flatMap(passenger => passenger[type].map(service => {
      const segment = segments.find(s => s.logicalFlightId === service.logicalFlightId);
      if(!segment) return;
      return {segment, passenger, service }
    }));

  if(logicalFlightId) {
    services = services.filter(x => x && x.segment?.logicalFlightId === logicalFlightId);
  }

  return services.filter(x => !!x);
}

export const getSelectedServices = ({ booking, ancillaries }: { booking: Booking, ancillaries: Ancillary[]}) => {
  if (!booking || !Array.isArray(booking.segments) || !Array.isArray(booking.passengers)) return {};

  const paxTotal = booking.passengers.filter(x => x.paxType === PassengerType.Adult || x.paxType === PassengerType.Child).length;
  const services = booking.segments.map(segment => {
    let mealTotal = 0, mealSelected = 0;
    // disregard the validation on meal selection in WCI mode
    if(segment.hasFreeMeal && booking.bookingMode !== 'WCI') {

      // find the leg that provide meals
      const anciSegment = ancillaries.find(x => x.logicalFlightId === segment.logicalFlightId)
      const meals = anciSegment?.passengers[0].meals
      const physicalLightFromMeals = !Array.isArray(meals) ? [] : meals.map(x => x.physicalFlightId)
      const uniquePhysicalFlightIds = physicalLightFromMeals.filter((v, i, a) => a.indexOf(v) === i)

      // calculate the number of meals to be expected based on the legs that provide meals
      mealTotal = paxTotal * uniquePhysicalFlightIds.length;
    }

    booking.passengers.forEach(pax => {
      if (Array.isArray(pax.meals)) {
        mealSelected += pax.meals.filter(x => x.logicalFlightId === segment.logicalFlightId).length;
      }
    })

    return {
      mealTotal,
      mealSelected,
      allMealSelected: mealSelected >= mealTotal,
      originCity: segment.originCity,
      destinationCity: segment.destinationCity,
    };
  })

  return {
    allMealSelected: services.map(x => x.allMealSelected).reduce((a, b) => a && b, true),
    segments: services,
  };
}


export type ServiceListItem = {
  logicalFlightId: number;
  route: string;
  currencyCode: string;
  service: AnciService;
};

export function getServiceList(key: AnciPassengerKeys, booking: Booking, ancillaries: Ancillary[]) {
  const result: ServiceListItem[] = [];

  if (!booking || !Array.isArray(booking.segments) || !booking.segments.length) return result;
  if (!booking.segments.find(x => x && x.permissions && x.permissions.canChangeExtras)) return result;

  if (!booking || !Array.isArray(booking.passengers)) return result;
  if (!Array.isArray(ancillaries) || !ancillaries.length) return result;

  ancillaries.forEach(ancil => {

    const bookingSegment = booking.segments.find(x => x.logicalFlightId === ancil.logicalFlightId);
    if(!bookingSegment) return;

    if (!Array.isArray(ancil.passengers) || !ancil.passengers.length) return;

    ancil.passengers.forEach(pax => {
      if (!Array.isArray(pax[key]) || !pax[key].length) return;

      pax[key].forEach(service => {
        const r = result.find(x => x.logicalFlightId === ancil.logicalFlightId && x.service.ssrCode === service.ssrCode);
        if(r) {
          r.service.amount += service.amount;
          return;
        }
        result.push({
          logicalFlightId: ancil.logicalFlightId,
          route: `${bookingSegment.originCode} - ${bookingSegment.destinationCode}`,
          currencyCode: booking.currencyCode,
          service: jsonHelper.cloneDeep(service)
        });
      })
    })
  });

  return result;
}